/**
 * Conditional display logic — the only place routing rules are evaluated on
 * the client (overview §4: "no hardcoded if in the UI"). Mirrors the server's
 * `displayRules.ts`.
 */
import { QuestionDef, QuestionOption } from './types';

/** Option values marked 'yes' or 'maybe' on a multiselect answer. */
export const selectedValues = (answer: unknown): string[] => {
  if (!answer || typeof answer !== 'object') return [];
  return Object.entries(answer as Record<string, string>)
    .filter(([, pref]) => pref === 'yes' || pref === 'maybe')
    .map(([value]) => value);
};

/**
 * Evaluate a question's display rule.
 * @param answersByKey maps a (non-segment) question key to its answer payload.
 */
export const isQuestionVisible = (
  question: QuestionDef,
  answersByKey: Record<string, unknown>
): boolean => {
  const rule = question.display;
  if ('always' in rule) return true;

  const refSelected = selectedValues(answersByKey[rule.showIf.question]);

  if ('includesAny' in rule.showIf) {
    return rule.showIf.includesAny.some((v) => refSelected.includes(v));
  }
  if ('selectedCountLte' in rule.showIf) {
    return refSelected.length <= rule.showIf.selectedCountLte;
  }
  if ('selectedCountGte' in rule.showIf) {
    return refSelected.length >= rule.showIf.selectedCountGte;
  }
  return true;
};

/**
 * The options a multiselect question should actually render, after applying its
 * `optionGroupFilter` (only options whose `group` is among the values marked
 * yes/maybe on the referenced question survive) and any per-option
 * `showIfSelected` self-gate (the option appears only when one of the listed
 * values is marked yes/maybe on this same question). Questions with neither
 * return their options unchanged.
 *
 * Drives both rendering and the form's visibility check — a question gated this
 * way with no options left is hidden. Mirrors the server's `displayRules.ts`.
 *
 * @param answersByKey maps a (non-segment) question key to its answer payload.
 */
export const visibleOptionsForQuestion = (
  question: QuestionDef,
  answersByKey: Record<string, unknown>
): QuestionOption[] => {
  const options = question.options ?? [];
  const filter = question.optionGroupFilter;
  const hasSelfGate = options.some((o) => o.showIfSelected !== undefined);
  if (!filter && !hasSelfGate) return options;
  const allowed = filter
    ? new Set(selectedValues(answersByKey[filter.question]))
    : null;
  const selfSelected = hasSelfGate
    ? new Set(selectedValues(answersByKey[question.key]))
    : null;
  return options.filter((o) => {
    if (o.showIfSelected && !o.showIfSelected.some((v) => selfSelected!.has(v)))
      return false;
    if (allowed && o.group !== undefined && !allowed.has(o.group)) return false;
    return true;
  });
};
